import { getRuntimeConfig } from '../runtimeConfig';
import { readAccessToken } from '../lib/auth';
import type {
  AuditEventInfo,
  ChangeMyPasswordRequest,
  CreateUserRequest,
  CreateUserResult,
  GrantRequest,
  KeycloakEventPage,
  KeycloakEventQuery,
  MailedPasswordResult,
  Me,
  MyApplication,
  MyProfile,
  PhotoResult,
  SetPasswordRequest,
  SystemDetail,
  SystemInfo,
  UpdateMyProfileRequest,
  UpdateUserRequest,
  UserDetail,
  UserSummary,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const { apiUrl } = getRuntimeConfig();
  const token = readAccessToken();
  const response = await fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      const problem: unknown = await response.json();
      if (
        typeof problem === 'object' &&
        problem !== null &&
        typeof (problem as { detail?: unknown }).detail === 'string'
      ) {
        detail = (problem as { detail: string }).detail;
      }
    } catch {
      // keep the status text
    }
    throw new ApiError(response.status, detail);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

function eventQuery(query: KeycloakEventQuery): string {
  const params = new URLSearchParams();
  if (query.first !== undefined) params.set('first', String(query.first));
  if (query.max !== undefined) params.set('max', String(query.max));
  if (query.type) params.set('type', query.type);
  if (query.client) params.set('client', query.client);
  if (query.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query.dateTo) params.set('dateTo', query.dateTo);
  if (query.search) params.set('search', query.search);
  return params.toString();
}

export const api = {
  me: () => apiFetch<Me>('/me'),
  myProfile: () => apiFetch<MyProfile>('/me/profile'),
  myApplications: () => apiFetch<MyApplication[]>('/me/applications'),
  updateMyProfile: (body: UpdateMyProfileRequest) =>
    apiFetch<void>('/me/profile', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  changeMyPassword: (body: ChangeMyPasswordRequest) =>
    apiFetch<void>('/me/password', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  users: () => apiFetch<UserSummary[]>('/users'),
  user: (id: string) => apiFetch<UserDetail>(`/users/${encodeURIComponent(id)}`),
  systems: () => apiFetch<SystemInfo[]>('/systems'),
  system: (id: string) => apiFetch<SystemDetail>(`/systems/${encodeURIComponent(id)}`),
  audit: () => apiFetch<AuditEventInfo[]>('/audit?limit=200'),
  keycloakEvents: (query: KeycloakEventQuery) =>
    apiFetch<KeycloakEventPage>(`/keycloak-events?${eventQuery(query)}`),
  userKeycloakEvents: (id: string, query: KeycloakEventQuery) =>
    apiFetch<KeycloakEventPage>(
      `/users/${encodeURIComponent(id)}/keycloak-events?${eventQuery(query)}`,
    ),
  createUser: (body: CreateUserRequest) =>
    apiFetch<CreateUserResult>('/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resetPassword: (id: string) =>
    apiFetch<MailedPasswordResult>(`/users/${encodeURIComponent(id)}/reset-password`, {
      method: 'POST',
    }),
  setPassword: (id: string, body: SetPasswordRequest) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}/password`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateUser: (id: string, body: UpdateUserRequest) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  activateUser: (id: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}/activate`, { method: 'POST' }),
  suspendUser: (id: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}/suspend`, { method: 'POST' }),
  reactivateUser: (id: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}/reactivate`, { method: 'POST' }),
  deleteUser: (id: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  uploadUserPhoto: (id: string, photo: Blob) =>
    apiFetch<PhotoResult>(`/users/${encodeURIComponent(id)}/photo`, {
      method: 'PUT',
      body: photo,
      headers: { 'Content-Type': photo.type },
    }),
  deleteUserPhoto: (id: string) =>
    apiFetch<void>(`/users/${encodeURIComponent(id)}/photo`, { method: 'DELETE' }),
  uploadMyPhoto: (photo: Blob) =>
    apiFetch<PhotoResult>('/me/photo', {
      method: 'PUT',
      body: photo,
      headers: { 'Content-Type': photo.type },
    }),
  deleteMyPhoto: () => apiFetch<void>('/me/photo', { method: 'DELETE' }),
  grant: (userId: string, body: GrantRequest) =>
    apiFetch<void>(`/users/${encodeURIComponent(userId)}/grants`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revoke: (userId: string, systemId: string, role: string) =>
    apiFetch<void>(
      `/users/${encodeURIComponent(userId)}/grants/${encodeURIComponent(systemId)}/${encodeURIComponent(role)}`,
      { method: 'DELETE' },
    ),
};
